Ext.define('SA.view.BarChartView', {
    alias: 'widget.bar-chart-view',
    extend: 'SA.view.SimpleContainer',

    cls: 'sa-bar-chart-view',
    everRenderedChart: false,

    items: [
        {
            xtype: 'load-mask',
            itemId: 'mask',
            borderColor: '#cfdef1',
            transparentBackground: true
        }, {
            xtype: 'simple-container',
            cls: 'sa-bar-chart-view-scroll',
            itemId: 'scroll',
            items: [{xtype: 'bar-chart'}]
        }
    ],

    initComponent() {
        this.callParent();

        this.chart = this.down('bar-chart');
        this.mask = this.down('#mask');
        this.scroll = this.down('#scroll');

        this.refreshBuffered = SA.createBuffered(this.refresh, 10);
        this.resizeContentBuffered = SA.createBuffered(this.resizeContent, 50);
    },

    initEvents() {
        this.callParent();

        this.mon(this.projectView, 'change:attribute', this.refreshBuffered, this);
        this.mon(this.projectView, 'change:location', this.refreshBuffered, this);
        this.mon(this.projectView, 'change:sortDirection', this.refreshBuffered, this);

        this.mon(SA.data.LocationAliases, 'change', this.refreshBuffered, this);

        this.mon(this.chart.el, 'click:bar', this.onClickBar, this, {normalized: false});
        this.mon(this.chart.el, 'click:xAxisTick', this.onClickXAxisTick, this, {normalized: false});
    },

    getData({callback = false} = {}) {
        var args = callback ? [{fn: this.refresh, scope: this}] : [],
            data = this.projectView.getReportData(...args);

        return Object.values(data);
    },

    getSvg() {
        return this.mask.getHidePromise().then(() => this.chart.getSvg());
    },

    maybeShowMenu({location, el, items}) {
        var menu = this.lastMenu,
            getElXY = () => Ext.fly(el).getXY(),
            initialElXY = getElXY(),
            elMoved = () => !Ext.Array.equals(initialElXY, getElXY());

        if (menu && this.lastMenuEl === el && menu.wasRecentlyVisible()) {
            return;
        }

        d3.select(el).classed('active', true);
        this.scroll.el.on('scroll', onScroll, this);

        this.lastMenuEl = el;

        this.lastMenu = Ext.widget({
            xtype: 'arrow-menu',
            alignment: 'right',
            items,
            positionEl: el,
            target: this,
            track: true,
            width: 270,

            defaults: {
                attribute: this.projectView.getAttribute(),
                deferHideParentMenus: () => this.lastMenu.destroy(),
                location,
                view: this.projectView
            },

            listeners: {
                beforerender: ({items}) => items.each(item => Ext.callback(item.beforeMenuShow, item)),

                beforedestroy: () => {
                    d3.select(el).classed('active', false);
                    this.scroll.el.un('scroll', onScroll, this);
                }
            }
        });

        function onScroll() {
            if (elMoved()) {
                this.lastMenu.destroy();
            }
        }
    },

    onBoxReady() {
        this.callParent();

        this.refresh();
    },

    onClickBar({target: el, detail}) {
        var location = detail.key;

        this.maybeShowMenu({location, el, items: [
            {
                xtype: 'menu-location-caption'
            }, {
                xtype: 'menu-create-map'
            }, {
                xtype: 'menu-create-ranking'
            }, {
                xtype: 'menu-remove-location',
                text: 'Remove from this View'
            }
        ]});
    },

    onClickXAxisTick({target: el, detail}) {
        var location = detail.key;

        this.maybeShowMenu({location, el, items: [
            {
                xtype: 'menu-add-remove-favorite-location'
            }, {
                xtype: 'menu-set-location-alias'
            }, {
                xtype: 'menu-remove-location',
                text: 'Remove from this View'
            }
        ]});
    },

    onResize() {
        this.callParent(arguments);

        this.resizeContentBuffered();
    },

    refresh() {
        var attribute = this.projectView.getAttribute(),
            canRenderChart = this.getData({callback: true}).every(location => attribute in location);

        if (!canRenderChart) {
            let maskSize = this.everRenderedChart ? this.scroll.getSize() : SA.ProjectView.getSpace();

            this.mask.setSize(maskSize);
            this.mask.show();
            return;
        }

        this.mask.hide();
        this.renderChart();
    },

    renderChart() {
        var projectView = this.projectView,
            Attributes = SA.data.AttributesCache,
            attribute = projectView.getAttribute(),
            attributeItem = Attributes.get(attribute),
            values = this.getData(),
            data = values.map(mapItem).sort(getSort(values)),
            yAxisTitle = attributeItem.name,
            space = SA.ProjectView.getSpace();

        if (!this.everRenderedChart) {
            this.everRenderedChart = true;
            this.mask.applyDefaultBackgroundColor();
        }

        this.chart.renderChart({
            space,
            attribute,
            data,
            formatCfg: getFormatCfg(),
            yAxisTitle
        });

        function mapItem(item) {
            var roundToHundredths = n => Math.round(n * 100) / 100,
                value = item[attribute];

            return {
                key: item.locationSeries,
                value: value !== null ? roundToHundredths(value) : null
            };
        }

        function getSort(values) {
            var valueMap = values.reduce((acc, item) => {
                    return Object.assign(acc, {
                        [item.locationSeries]: item[attribute]
                    });
                }, {}),
                direction = projectView.getSortDirection(),
                locationSorter = SA.data.Sort.createLocationSorter(valueMap, direction);

            return (a, b) => locationSorter(a.key, b.key);
        }

        function getFormatCfg() {
            return {
                integersOnly: attributeItem.attributeFormat === 'integer',

                valueFormatter(value) {
                    var {attributeFormat, formatString} = attributeItem;

                    if (!Ext.isNumber(value)) {
                        return 'N/A';
                    }
                    else if (attributeFormat === 'currency') {
                        return '$' + formatWithThousandSuffix(value);
                    }
                    else if (attributeFormat === 'integer') {
                        return formatWithThousandSuffix(value).replace(/\.0+$/, '');
                    }
                    else if (attributeFormat === 'percent') {
                        return Attributes.formatValue(value, formatString);
                    }

                    return formatWithThousandSuffix(value);

                    function formatWithThousandSuffix(value) {
                        var {mantissa, suffix} = SA.Number.toMantissaAndThousandSuffix(value);

                        if (mantissa < 10) {
                            return mantissa.toFixed(2) + suffix;
                        }
                        else if (mantissa < 100) {
                            return mantissa.toFixed(1) + suffix;
                        }

                        return mantissa.toFixed(0) + suffix;
                    }
                }
            };
        }
    },

    resizeContent() {
        SA.Html.setMaxSize(this.scroll.el.dom, SA.ProjectView.getSpace());

        this.refresh();
    }
});
